import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ModuleService } from "../../../services/user/modules.services";
import { handleNotify, TOASTER_POSITION, TOASTER_TYPE } from "../../common/notification/toaster_notify.component";
import DeleteModelPopupComponent from "../common_model_popup/deletemodelpopup.component";
import { handleCopy, handleExcel, handlePDF, handlePrint } from "../../../utils/filedownload";

const ModulesComponent = () => {
  const navigate = useNavigate();
  const selectedStore = useSelector(state => state?.user?.selectedStoreId);
  const [searchText, setSearchText] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState(null);
  const rowsPerPage = 5;

  // Fetch data
  useEffect(() => {
    const fetchModuleData = async () => {
      try {
        setLoading(true);
        const response = await ModuleService.getAllModules(selectedStore);
        setData(response);
      } catch (error) {
        handleNotify("Error fetching modules", TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchModuleData();
  }, [selectedStore]);

  // Handle pagination
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;

  // // Filter data 
  const filteredData = data.filter(row =>
    row.moduleName.toLowerCase().includes(searchText.toLowerCase()) ||
    row.prefixNo.toLowerCase().includes(searchText.toLowerCase()) ||
    row.financialYear.toLowerCase().includes(searchText.toLowerCase()) ||
    row.moduleNumber.toString().includes(searchText)
  );

  const currentData = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Handle Edit
  const handleEditClick = (item) => {
    navigate('/modules/edit', { state: { moduleId: item?.moduleId } });
  };

  // Handle Delete
  const handleDeleteClick = (module) => {
    setModuleToDelete(module);
    setShowModal(true);
  };

  //handle delete
  const handleDeleteConfirm = async () => {
    if (moduleToDelete) {
      try {
        await ModuleService.deleteModule(moduleToDelete.moduleId);
        setData(prevData => prevData.filter(module => module.moduleId !== moduleToDelete.moduleId));
        handleNotify("Module deleted successfully!", TOASTER_TYPE.SUCCESS, TOASTER_POSITION.TOP_RIGHT);
        setShowModal(false);
      } catch (error) {
        handleNotify(error.message || 'Error while deleting module.', TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
      }
    }
  };

  const handleAddNewClick = () => {
    navigate('/modules/create');
  };

  // Columns for DataTable
  const columns = [
    {
      name: "Module",
      selector: (row) => row.moduleName,
      sortable: true,
    },
    {
      name: "Prefix No",
      selector: (row) => row.prefixNo,
      sortable: true,
    },
    {
      name: "Financial Year",
      selector: (row) => row.financialYear,
      sortable: true,
    },
    {
      name: "Number",
      selector: (row) => row.moduleNumber,
      sortable: true,
    },
    {
      name: "Actions",
      button: true,
      width: "15%",
      cell: (row) => (
        <div className="d-flex justify-content-center gap-2">
          <button className="btn btn-primary" onClick={() => handleEditClick(row)}>
            Edit
          </button>
          <button className="btn btn-danger" onClick={() => handleDeleteClick(row)}>
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper m-0 p-4">
      <div className="page-content">
        <div className="page-breadcrumb d-flex align-items-center mb-3">
          <div className="breadcrumb-title pe-3 text-primary fw-bold fs-4">
            Modules
          </div>
          <div className="ms-auto">
            <button className="btn btn-primary" onClick={handleAddNewClick}>
              New +
            </button>
          </div>
        </div>
        <div className="card radius-10 border-top border-0 border-4 border-primary p-4">
          <div className="card-body">
            <div className="d-flex justify-content-center flex-column flex-md-row mb-3">
              <div className="btn-group me-2 mb-3 mb-md-0 mb-sm-0">
                <button className="btn btn-outline-secondary" onClick={() => handleCopy(filteredData)}>Copy</button>
                <button className="btn btn-outline-secondary" onClick={() => handleExcel(filteredData)}>Excel</button>
                <button className="btn btn-outline-secondary" onClick={() => handlePDF(filteredData)}>PDF</button>
                <button className="btn btn-outline-secondary" onClick={handlePrint}>Print</button>
              </div>

              <div className="d-flex ms-auto align-items-center justify-content-center flex-md-row">
                <div className="m-2">
                  <span className="fw-bold">Search:</span>
                </div>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search 🔍..."
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                />
              </div>
            </div>

            <div className="table-responsive">
              <DataTable
                columns={columns}
                data={currentData}
                fixedHeader
                responsive
                highlightOnHover
                sortIcon={<i className="fadeIn animated bx bx-sort" />}
                progressPending={loading}
              />
            </div>

            {showModal && (
              <DeleteModelPopupComponent
                show={showModal}
                onClose={() => setShowModal(false)}
                onConfirm={handleDeleteConfirm}
                title="Confirm"
                confirmText="Delete"
                Message="Are you sure you want to delete this module?"
              />
            )}

            {/* Pagination Controls */}
            <div className="d-flex mt-4 justify-content-end">
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }, (_, index) => (
                  <button
                    key={index}
                    className={`btn ${currentPage === index + 1 ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => handlePageChange(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModulesComponent;
